// <!-- glow-menopause-style-enhancements -->
import React, { useState } from 'react';
import { ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ConditionExitPopup from '../components/ConditionExitPopup';
import TrustStrip from '../components/TrustStrip';
import ClinicianCard from '../components/ClinicianCard';
import ServicesDropdown from '../components/ServicesDropdown';

const PMOSLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToBooking = () => {
    window.location.href = '/book';
  };

  const painPoints = [
    {
      title: 'Weight that refuses to budge',
      pain: 'Are you exhausted from trying every diet, every workout, every "hack" only to watch the scale stay stuck or creep higher?',
      description: 'PMOS changes how your body processes insulin and stores fat. It is not about willpower. Your metabolism is working against you.'
    },
    {
      title: 'Cycles that make no sense',
      pain: 'Are you tired of never knowing when your period will show up, or wondering if it will come at all this month?',
      description: 'Irregular or missing periods are one of the hallmark signs of PMOS. Your body is signaling that something hormonal needs attention.'
    },
    {
      title: 'Acne that makes you feel like a teenager again',
      pain: 'Are you hiding your skin under makeup, canceling plans, or avoiding mirrors because of stubborn breakouts?',
      description: 'Hormonal acne from PMOS shows up along your jawline, chin, and cheeks. It is deeper, more painful, and does not respond to typical skincare.'
    },
    {
      title: 'Hair in all the wrong places',
      pain: 'Are you spending hours removing facial hair, feeling embarrassed, or dreading intimacy because of unwanted growth?',
      description: 'Excess hair on your face, chest, or stomach is driven by elevated androgens. It is a medical symptom, not a personal failing.'
    },
    {
      title: 'Hair loss where you want it most',
      pain: 'Are you watching your hairline thin, finding clumps in the shower, or feeling like you are losing a part of yourself?',
      description: 'Thinning hair on your head while growing it elsewhere is a cruel contradiction of PMOS. It affects how you see yourself every single day.'
    },
    {
      title: 'Fertility fears and uncertainty',
      pain: 'Are you worried that PMOS means you will never be able to have children, or struggling to conceive despite trying everything?',
      description: 'PMOS is the leading cause of ovulatory infertility, but it does not mean pregnancy is impossible. It means you need the right support.'
    }
  ];

  const emotionalImpact = [
    {
      area: 'Your confidence',
      impact: 'Avoiding photos, dreading social events, feeling like your body has betrayed you'
    },
    {
      area: 'Your relationships',
      impact: 'Mood swings pushing people away, feeling too exhausted for intimacy, hiding your symptoms from partners'
    },
    {
      area: 'Your mental health',
      impact: 'Anxiety about your body, depression from feeling unheard, shame about symptoms you cannot control'
    },
    {
      area: 'Your daily life',
      impact: 'Brain fog making work harder, fatigue stealing your energy, feeling like you are just surviving instead of living'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Tell us your story',
      description: 'Share what you have been experiencing, what you have tried, and what keeps you up at night. We listen without judgment.'
    },
    {
      number: '2',
      title: 'Get a real evaluation',
      description: 'Comprehensive hormone and metabolic assessment to understand exactly what is happening in your body.'
    },
    {
      number: '3',
      title: 'Receive your personalized plan',
      description: 'A treatment approach built around your specific PMOS type, symptoms, and goals. Not a one size fits all protocol.'
    },
    {
      number: '4',
      title: 'Ongoing support as you heal',
      description: 'Regular check ins, plan adjustments, and a care team that actually knows your name and your story.'
    }
  ];

  const proofPoints = [
    {
      stat: '1 in 10',
      title: 'Women have PMOS',
      description: 'You are not alone. PMOS is one of the most common hormonal disorders in women, yet most feel isolated and unheard.'
    },
    {
      stat: '70%',
      title: 'Go undiagnosed',
      description: 'Most women with PMOS are never properly diagnosed. Their symptoms get dismissed, minimized, or blamed on lifestyle.'
    },
    {
      stat: '4 wks',
      title: 'To start feeling different',
      description: 'With the right treatment plan, most women begin noticing improvements in energy, mood, and symptoms within weeks.'
    }
  ];

  const faqs = [
    {
      question: 'How do I know if I actually have PMOS?',
      answer: 'PMOS is diagnosed based on a combination of symptoms, hormone levels, and sometimes ultrasound findings. If you have irregular periods, signs of excess androgens like acne or unwanted hair, or difficulty losing weight, it is worth getting evaluated. Many women go years without a proper diagnosis.'
    },
    {
      question: 'Will I be able to lose weight with PMOS?',
      answer: 'Yes, but it requires addressing the underlying insulin resistance and hormonal imbalances first. Crash diets and extreme exercise often backfire with PMOS. We create sustainable plans that work with your body, not against it.'
    },
    {
      question: 'Can I get pregnant if I have PMOS?',
      answer: 'Absolutely. PMOS is the most common cause of ovulatory infertility, but with proper treatment many women with PMOS conceive successfully. The key is addressing ovulation and metabolic health.'
    },
    {
      question: 'What treatments do you offer for PMOS?',
      answer: 'We take a comprehensive approach that may include hormone optimization, metabolic support, targeted supplements, and lifestyle guidance. Every plan is personalized based on your specific PMOS presentation and goals.'
    },
    {
      question: 'I have tried everything. How is this different?',
      answer: 'Most women with PMOS have been given generic advice that does not account for their unique hormonal situation. We start by actually understanding what is happening in YOUR body, then create a plan that addresses YOUR specific imbalances. No more one size fits all protocols.'
    }
  ];

  return (
    <div className="pcos-page">
      {/* Header */}
      <header className="pcos-header">
        <div className="pcos-container pcos-header-inner">
          <a href="/" className="pcos-brand">
            <img
              src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
              alt="Glow Health Logo"
              className="brand-symbol"
            />
            <span>Glow Health</span>
          </a>
          <div className="pcos-header-actions">
            <ServicesDropdown testIdPrefix="pmos-nav-services" />
            <button className="pcos-btn pcos-btn-primary" onClick={scrollToBooking}>
              Book Your Free Consultation
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="pcos-hero">
          <div className="pcos-container pcos-hero-split">
            <div className="pcos-hero-content">
              <span className="pcos-eyebrow">PMOS Support (formerly PCOS)</span>
              <h1 className="pcos-hero-headline">
                You are not crazy. You are not lazy.<br />
                <span className="pcos-highlight">You have PMOS.</span>
              </h1>
              <p className="pcos-hero-sub">
                If you are exhausted from being told to "just lose weight" while your body fights you at every turn, if you are tired of doctors dismissing your symptoms, if you feel like you are failing at something that should be simple... you are not the problem. Your hormones are.
              </p>
              <div className="pcos-hero-actions">
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking}>
                  Get Real Answers
                  <ArrowRight size={18} />
                </button>
                <a href="#pain" className="pcos-btn pcos-btn-secondary">See If This Sounds Like You</a>
              </div>
              <p className="pcos-hero-note">Free consultation. No judgment. Finally, someone who gets it.</p>
              <div className="meno-hero-credentials" data-testid="pmos-hero-credentials">
                <p><strong>Utah-licensed clinician</strong> experienced in PMOS care.</p>
                <p>Serving women in Utah via secure HIPAA video visits.</p>
              </div>
            </div>
            <div className="pcos-hero-image">
              <img
                src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=640&h=760&fit=crop&auto=format"
                alt="A woman who finally got the care she needed"
                loading="lazy"
              />
              <div className="pcos-hero-image-badge">
                "I was finally heard."
              </div>
            </div>
          </div>
        </section>

        <TrustStrip />

        {/* Three-panel journey strip */}
        <section className="meno-journey" data-testid="pmos-journey">
          <div className="pcos-container">
            <div className="meno-journey-grid">
              <figure className="meno-journey-panel" data-testid="pmos-journey-panel-1">
                <div className="meno-journey-image">
                  <img src="/pmos-panel-1.png" alt="A woman sitting quietly with confusing lab results" loading="lazy" />
                </div>
                <figcaption>You are not failing. Your hormones are.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="pmos-journey-panel-2">
                <div className="meno-journey-image">
                  <img src="/pmos-panel-2.png" alt="A woman on a warm telehealth video visit with a clinician who understands PMOS" loading="lazy" />
                </div>
                <figcaption>Talk with a clinician who understands PMOS.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="pmos-journey-panel-3">
                <div className="meno-journey-image">
                  <img src="/pmos-panel-3.png" alt="A woman walking peacefully through a sunlit garden, restored" loading="lazy" />
                </div>
                <figcaption>A plan tailored to your body and your cycle.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Specialist value prop */}
        <section className="meno-specialist" data-testid="pmos-specialist">
          <div className="pcos-container">
            <p><strong>Not a general telehealth clinic.</strong> We specialize in PMOS (formerly PCOS), so your symptoms are not minimized as &quot;just lose some weight.&quot;</p>
          </div>
        </section>


        {/* Diagnosis rename callout — added Feb 2026 per The Lancet */}
        <section className="pmos-rename-callout" data-testid="pmos-rename-callout">
          <div className="pcos-container">
            <div className="pmos-rename-card">
              <div className="pmos-rename-badge">Updated diagnosis name</div>
              <h2>PCOS has been officially renamed to PMOS.</h2>
              <p>
                In May 2026, a global consensus published in <em>The Lancet</em> renamed Polycystic Ovary Syndrome (PCOS) to <strong>Polyendocrine Metabolic Ovarian Syndrome (PMOS)</strong>. The old name was misleading: it suggested the issue was only ovarian cysts, when in reality this is a whole-body endocrine and metabolic condition. The new name reflects what is actually happening in your body, and what good care must address.
              </p>
              <p className="pmos-rename-footnote">
                If you were diagnosed with PCOS, that is the same condition we are talking about here. Nothing about your symptoms or care has changed, only the name.
              </p>
            </div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="pcos-section pcos-pain-section" id="pain">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Does This Sound Like You?</span>
              <h2>The symptoms no one talks about honestly</h2>
              <p>PMOS is more than a diagnosis. It is waking up every day in a body that feels like it is working against you.</p>
            </div>
            <div className="pcos-pain-grid">
              {painPoints.map((point, index) => (
                <div key={index} className="pcos-pain-card">
                  <h3>{point.title}</h3>
                  <p className="pcos-pain-question">{point.pain}</p>
                  <p className="pcos-pain-explain">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emotional Impact */}
        <section className="pcos-section pcos-impact-section">
          <div className="pcos-container">
            <div className="pcos-impact-wrap">
              <div className="pcos-impact-content">
                <span className="pcos-eyebrow">The Hidden Cost</span>
                <h2>PMOS does not just affect your body. It affects your entire life.</h2>
                <p className="pcos-impact-intro">
                  The physical symptoms are hard enough. But what about everything else PMOS takes from you?
                </p>
              </div>
              <div className="pcos-impact-grid">
                {emotionalImpact.map((item, index) => (
                  <div key={index} className="pcos-impact-card">
                    <h4>{item.area}</h4>
                    <p>{item.impact}</p>
                  </div>
                ))}
              </div>
              <p className="pcos-impact-cta">
                <strong>This is not just about hormones. This is about getting your life back.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Quote */}
        <section className="pcos-section pcos-quote-section" style={{backgroundImage: "url('/quote-bg-pmos.png')"}}>
          <div className="pcos-container">
            <div className="pcos-quote-wrap">
              <blockquote>
                <p>"I spent 8 years being told my weight was the problem. Turns out the problem was my hormones, and no one bothered to check."</p>
                <cite>Real patient story</cite>
              </blockquote>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="pcos-section" id="plan">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">How We Help</span>
              <h2>A different approach to PMOS care</h2>
              <p>No more generic advice. No more feeling dismissed. No more figuring it out alone.</p>
            </div>
            <div className="pcos-steps-grid">
              {steps.map((step, index) => (
                <div key={index} className="pcos-step-card">
                  <div className="pcos-step-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
            <div className="pcos-steps-cta">
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking}>
                Book Your Free Consultation
                <ArrowRight size={18} />
              </button>
              <p className="pcos-cta-note">No commitment. No pressure. Just answers.</p>
            </div>
          </div>
        </section>

        {/* Proof Points */}
        <section className="pcos-section pcos-proof-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">The Reality</span>
              <h2>You are not alone, even though it feels that way</h2>
            </div>
            <div className="pcos-proof-grid">
              {proofPoints.map((point, index) => (
                <div key={index} className="pcos-proof-card">
                  <strong>{point.stat}</strong>
                  <h3>{point.title}</h3>
                  <p>{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClinicianCard />

        {/* Who this is right for */}
        <section className="pcos-section meno-fit-section" data-testid="pmos-fit-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">A Quick Honest Check</span>
              <h2>Who My Glow Health is right for</h2>
              <p>We are not for everyone, and that is the point. Here is who tends to feel the biggest shift working with us.</p>
            </div>
            <div className="meno-fit-grid">
              <div className="meno-fit-card meno-fit-yes" data-testid="pmos-fit-yes">
                <h3>This is right for you if</h3>
                <ul>
                  <li>You live in Utah and want secure telehealth visits.</li>
                  <li>You suspect PMOS (formerly PCOS) is driving how you feel.</li>
                  <li>You want a clinician who specializes in this, not a generalist.</li>
                  <li>You are open to evidence-based hormone, insulin, and metabolic support.</li>
                  <li>You want a plan tailored to your body, not a template.</li>
                </ul>
              </div>
              <div className="meno-fit-card meno-fit-no" data-testid="pmos-fit-no">
                <h3>This is probably not for you if</h3>
                <ul>
                  <li>You are looking for a one-time prescription with no labs.</li>
                  <li>You want quick weight-loss medication only, with no underlying workup.</li>
                  <li>You are not interested in addressing root-cause hormone balance.</li>
                  <li>You prefer a one-size-fits-all wellness program.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>


        {/* CTA Section */}
        <section className="pcos-section pcos-cta-section" id="book">
          <div className="pcos-container">
            <div className="pcos-cta-wrap">
              <div className="pcos-cta-content">
                <span className="pcos-eyebrow">Take The First Step</span>
                <h2>Stop fighting your body alone</h2>
                <ul className="pcos-check-list">
                  <li>
                    <CheckCircle size={18} />
                    <span>Finally understand what is actually happening with your hormones</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Get a plan that accounts for YOUR specific PMOS symptoms</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Work with providers who specialize in PMOS, not generalists who brush you off</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Stop feeling crazy and start feeling heard</span>
                  </li>
                </ul>
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking}>
                  Book Your Free Consultation
                  <ArrowRight size={18} />
                </button>
                <p className="pcos-cta-note">Free & confidential. Limited spots available each week.</p>
              </div>
              <div className="pcos-cta-visual">
                <div className="pcos-guarantee-card">
                  <h3>Our Promise</h3>
                  <p>If you do not feel meaningfully better within 60 days of starting your plan, we will keep working with you for free until you do. No questions asked.</p>
                </div>
                <div className="pcos-testimonial-card">
                  <p>"For the first time in 10 years, I feel like someone actually understands what I am going through."</p>
                  <span>PMOS patient, age 34</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="pcos-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Questions</span>
              <h2>What you are probably wondering</h2>
            </div>
            <div className="pcos-faq-list">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className={`pcos-faq-item ${openFaq === index ? 'open' : ''}`}
                >
                  <button 
                    className="pcos-faq-question"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span>{faq.question}</span>
                    {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div className="pcos-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="pcos-section pcos-final-cta">
          <div className="pcos-container">
            <div className="pcos-final-cta-content">
              <h2>You have suffered long enough</h2>
              <p>
                Every day you wait is another day of fighting a battle you were never meant to fight alone. Your hormones are not your fault. But getting help? That is your choice.
              </p>
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking}>
                Book Your Free Consultation Now
                <ArrowRight size={18} />
              </button>
              <p className="pcos-final-note">15 minutes could change everything.</p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="pcos-footer">
        <div className="pcos-container pcos-footer-inner">
          <span>© {new Date().getFullYear()} Glow Health LLC</span>
          <div className="pcos-footer-links">
            <a href="/privacy-policy">Privacy Policy</a>
            <span>·</span>
            <a href="/terms-of-service">Terms of Service</a>
          </div>
        </div>
      </footer>
      <ConditionExitPopup condition="pmos" />
    </div>
  );
};

export default PMOSLanding;
